import i18n from '../../../i18n/i18n';
import { tr } from '../../../i18n/i18n';
import React, { useMemo } from 'react';
import { AppButton } from '../../index';
import { MESSAGE_QUEUE_STATUS } from '../../../constant/message-queue.constant';
import type { IJobRecord, IJobResultError } from '../../../interface';
import { useTranslation } from 'react-i18next';

interface UploadSummaryProps {
  /**
   * Job record containing upload results
   */
  job: IJobRecord | null;

  /**
   * Optional callback when download original file button is clicked
   */
  onDownloadOriginal?: () => void;

  /**
   * Optional callback when download error report button is clicked
   */
  onDownloadErrors?: () => void;
}

interface StatusBannerProps {
  status: string;
  successCount: number;
  failureCount: number;
}

/**
 * Download error report as CSV file
 */
const downloadErrorReport = (errors: IJobResultError[], originalFilename: string) => {
  if (!errors || errors.length === 0) return;

  const headers = 'Row,Error Messages\n';
  const csvContent = errors
    .map((e: any) => {
      // Handle different error formats from API
      let errorMessages: string[] = [];
      if (e.messages) {
        errorMessages = e.messages;
      } else if (e.errors) {
        errorMessages = e.errors;
      } else if (e.error) {
        errorMessages = [e.error];
      }

      // Sanitize and escape each message
      const cleanMessages = errorMessages
        .map((msg) =>
          msg
            .replace(/[\r\n]+/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/"/g, '""')
            .trim(),
        )
        .join(', ');
      // Wrap the message in double quotes to ensure it's one column
      return `${e.row},"${cleanMessages}"`;
    })
    .join('\n');

  const blob = new Blob([headers + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  const errorFilename = originalFilename.replace(/\.xlsx?$/, '-errors.csv');
  link.setAttribute('download', errorFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Status Banner Component
 */
const StatusBanner: React.FC<StatusBannerProps> = ({ status, successCount, failureCount }) => {
  const { t } = useTranslation();
  const bannerConfig = useMemo(() => {
    if (status === MESSAGE_QUEUE_STATUS.FAILED) {
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        title: t('ui.job_failed', 'Job Failed'),
        message: i18n.t('msg.the_upload_process_failed_please_review_the_erro', 'The upload process failed. Please review the errors below.'),
        icon: 'M10 1.9C5 1.9 1 6.3 1 11.7s4 9.8 9 9.8 9-4.4 9-9.8S15 1.9 10 1.9zm0 17.7c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm-1-12h2v6h-2zm0 8h2v2h-2z',
      };
    }
    if (status === MESSAGE_QUEUE_STATUS.COMPLETED && successCount === 0 && failureCount === 0) {
      return {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        title: t('ui.no_records_processed', 'No Records Processed'),
        message: i18n.t('msg.the_upload_completed_but_no_records_were_process', 'The upload completed, but no records were processed.'),
        icon: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z',
      };
    }
    if (status === MESSAGE_QUEUE_STATUS.COMPLETED && successCount > 0 && failureCount === 0) {
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        title: t('ui.upload_successful', 'Upload Successful'),
        message: i18n.t('msg.all_records_were_created_successfully', 'All records were created successfully.'),
        icon: 'M10 1.9C5 1.9 1 6.3 1 11.7s4 9.8 9 9.8 9-4.4 9-9.8S15 1.9 10 1.9zm-1.5 13.2l-3.3-3.3 1.4-1.4 1.9 1.9 4.3-4.3 1.4 1.4-5.7 5.7z',
      };
    }
    if (status === MESSAGE_QUEUE_STATUS.COMPLETED && failureCount > 0) {
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        title: t('ui.completed_with_errors', 'Completed with Errors'),
        message: i18n.t('msg.the_upload_finished_but_some_rows_could_not_be_p', 'The upload finished, but some rows could not be processed.'),
        icon: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
      };
    }
    return {
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      title: t('ui.in_progress', 'In Progress'),
      message: i18n.t('msg.the_file_is_currently_being_processed', 'The file is currently being processed.'),
      icon: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z',
    };
  }, [status, successCount, failureCount]);

  return (
    <div
      className={`p-4 rounded-lg ${bannerConfig.bgColor} ${bannerConfig.textColor} flex items-start`}
    >
      <svg className="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
        <path d={bannerConfig.icon}></path>
      </svg>
      <div>
        <h3 className="font-bold font-poppins">{bannerConfig.title}</h3>
        <p className="text-sm font-poppins">{bannerConfig.message}</p>
      </div>
    </div>
  );
};

/**
 * Summary Statistics Component
 */
const SummaryStats: React.FC<{ job: IJobRecord }> = ({ job }) => {
  const { t } = useTranslation();
  const successCount = job.result?.successCount || 0;
  const failureCount = job.result?.failureCount || 0;
  const total = successCount + failureCount;

  return (
    <div className="grid grid-cols-3 gap-4 text-center my-6">
      <div>
        <p className="text-sm text-gray-500 font-poppins">{t('common.total_rows', 'Total Rows')}</p>
        <p className="text-2xl font-bold text-gray-800 font-poppins">{total}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500 font-poppins">{t('common.successful', 'Successful')}</p>
        <p className="text-2xl font-bold text-green-600 font-poppins">{successCount}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500 font-poppins">{t('common.failed', 'Failed')}</p>
        <p className="text-2xl font-bold text-red-600 font-poppins">{failureCount}</p>
      </div>
    </div>
  );
};

/**
 * Error Table Component
 */
const ErrorTable: React.FC<{ errors: IJobResultError[] }> = ({ errors }) => {
  const { t } = useTranslation();
  if (!errors || errors.length === 0) return null;

  return (
    <div className="mt-6">
      <h3 className="font-semibold text-gray-800 mb-2 font-poppins">{t('common.error_details', 'Error Details')}</h3>
      <div className="border rounded-lg max-h-60 overflow-y-auto">
        <table className="w-full text-sm font-poppins">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="p-3 text-left w-24 font-poppins">{t('common.label_row', 'Row')}</th>
              <th className="p-3 text-left font-poppins">{t('common.error_messages', 'Error Message(s)')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {errors.map((error: any, index) => {
              // Handle different error formats from API
              let errorMessages: string[] = [];
              if (error.messages) {
                errorMessages = error.messages;
              } else if (error.errors) {
                errorMessages = error.errors;
              } else if (error.error) {
                errorMessages = [error.error];
              }

              return (
                <tr key={index} className="bg-white hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-700 font-poppins">{error.row}</td>
                  <td className="p-3 text-red-700 font-poppins">
                    {errorMessages.join(', ') || tr('g.unknown_error', 'Unknown error')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Reusable Upload Summary Component
 *
 * Displays comprehensive upload job results including status banner,
 * statistics, error details, and download options.
 *
 * @example
 * ```tsx
 * <UploadSummary
 *   job={selectedJob}
 *   onDownloadOriginal={() => window.open(job.meta_data.Location, '_blank')}
 *   onDownloadErrors={() => downloadErrorReport(errors, filename)}
 * />
 * ```
 */
const UploadSummary: React.FC<UploadSummaryProps> = ({
  job,
  onDownloadOriginal,
  onDownloadErrors,
}) => {
  const { t } = useTranslation();
  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 font-poppins">{t('common.no_job_data', 'No job data available.')}</p>
      </div>
    );
  }

  const originalFilename = job.meta_data?.filename || 'upload.xlsx';
  const errors = job.result?.errors || [];
  const hasOriginalFile = job.meta_data?.Location;

  const handleDownloadOriginal = () => {
    if (onDownloadOriginal) {
      onDownloadOriginal();
    } else if (hasOriginalFile) {
      window.open(job.meta_data.Location, '_blank');
    }
  };

  const handleDownloadErrors = () => {
    if (onDownloadErrors) {
      onDownloadErrors();
    } else {
      downloadErrorReport(errors, originalFilename);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white p-6 rounded-xl">
        <StatusBanner
          status={job.status}
          successCount={job.result?.successCount || 0}
          failureCount={job.result?.failureCount || 0}
        />

        <SummaryStats job={job} />

        <div className="flex justify-center gap-4 border-t pt-6">
          {hasOriginalFile && (
            <AppButton
              label={t('common.button_download_original', 'Download Original File')}
              onClick={handleDownloadOriginal}
              outlined
              icon="pi pi-download"
            />
          )}
          {errors.length > 0 && (
            <AppButton
              label={t('common.button_download_error', 'Download Error Report')}
              onClick={handleDownloadErrors}
              severity="danger"
              outlined
              icon="pi pi-file-excel"
            />
          )}
        </div>

        <ErrorTable errors={errors} />
      </div>
    </div>
  );
};

export default UploadSummary;
